/**
 * Custom hook for filtering events
 * Handles search query, event type filtering, theme filtering and pagination
 *
 * Similar to use-working-groups-filter but adapted for events
 */

'use client';

import type { Theme } from '@uicwebmaster/taxonomy-themes';
import { buildThemeTree } from '@uicwebmaster/taxonomy-themes/utils';
import { useEffect, useMemo, useState, useTransition } from 'react';
import type { Event } from '../types';

const STRIP_HTML_TAGS = /<[^>]*>/g;

const stripHtml = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  return value.replace(STRIP_HTML_TAGS, '');
};

// Helper to normalize eventType to array (handles both single object and array)
const normalizeEventType = (eventType: Event['eventType']) => {
  if (!eventType) return [];
  return Array.isArray(eventType) ? eventType : [eventType];
};

interface ThemeNode {
  id: string;
  name: string;
  color?: string;
  level?: number;
  children?: ThemeNode[];
  [key: string]: any;
}

interface UseEventsFilterOptions {
  events: Event[];
  pageSize?: number;
  initialShowPastEvents?: boolean;
  initialEventTypes?: string[]; // Pre-select event types by ID on mount
  initialEventTypeNames?: string[]; // Pre-select event types by name on mount
  themes?: Theme[]; // Flat array of themes - tree will be built internally
}

export function useEventsFilter({
  events,
  pageSize = 12,
  initialShowPastEvents = false,
  initialEventTypes = [],
  initialEventTypeNames = [],
  themes = [],
}: UseEventsFilterOptions) {
  const safePageSize = Math.max(1, pageSize);

  // Extract unique event types from events first (needed for name-to-ID conversion)
  const eventTypes = useMemo(() => {
    const typeMap = new Map<string, { id: string; name: string; colour?: string | null }>();

    events.forEach(event => {
      const normalizedTypes = normalizeEventType(event.eventType);
      normalizedTypes.forEach(type => {
        if (type?.id && type?.name && !typeMap.has(type.id)) {
          typeMap.set(type.id, {
            id: type.id,
            name: type.name,
            colour: type.eventTypeColor || null,
          });
        }
      });
    });

    return Array.from(typeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  // Convert initial event type names to IDs
  const initialEventTypeIds = useMemo(() => {
    if (initialEventTypeNames.length > 0) {
      const normalizedNames = initialEventTypeNames.map(name => name.toLowerCase().trim());
      return eventTypes
        .filter(type => normalizedNames.includes(type.name.toLowerCase().trim()))
        .map(type => type.id);
    }
    return initialEventTypes;
  }, [initialEventTypes, initialEventTypeNames, eventTypes]);

  // Filter state
  const [query, setQuery] = useState('');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(initialEventTypeIds);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [themeFilterMode, setThemeFilterMode] = useState<'OR' | 'AND'>('OR');
  const [showPastEvents, setShowPastEvents] = useState(initialShowPastEvents);
  const [currentPage, setCurrentPage] = useState(1);

  // Use transition for non-urgent filter updates
  const [isPending, startTransition] = useTransition();

  const normalizedQuery = query.trim().toLowerCase();

  // Build theme tree from flat themes array
  const themesTree = useMemo(() => {
    if (!themes || themes.length === 0) return [];
    return buildThemeTree(themes);
  }, [themes]);

  /**
   * Extract themes from events and build hierarchical tree with direct ancestors only
   * 
   * Reasoning Path:
   * 1. Filter events based on showPastEvents to determine visible events
   * 2. Extract unique theme IDs from visible events
   * 3. For each theme, find ONLY its direct ancestors (not siblings or descendants)
   * 4. Build filtered tree containing only: themes in events + their direct ancestors
   * 5. Propagate colours from parents to children
   * 
   * OPTIMISATION: This now only includes direct ancestors, not entire branches
   */
  const filteredThemes = useMemo(() => {
    // First, filter events based on showPastEvents to get visible events
    const now = new Date();
    const visibleEventsForThemes = events.filter(event => {
      if (!showPastEvents) {
        const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
        if (!endTimestamp) return false;
        const eventEnd = new Date(endTimestamp * 1000);
        if (eventEnd < now) return false;
      }
      return true;
    });

    // Extract unique theme IDs from visible events only
    const themeIdsInEvents = new Set<string>();

    visibleEventsForThemes.forEach(event => {
      const eventThemes = event.theme ?? [];
      eventThemes.forEach((theme: any) => {
        if (theme?.id) {
          themeIdsInEvents.add(theme.id);
        }
      });
    });

    // If themesTree is available, build filtered tree with direct ancestors only
    if (themesTree && themesTree.length > 0) {
      /**
       * Helper: find all ancestor IDs for a given theme (direct path to root)
       * Returns ancestors in order from immediate parent to root
       */
      const findAncestors = (
        nodes: ThemeNode[],
        targetId: string,
        currentPath: string[] = []
      ): Set<string> => {
        for (const node of nodes) {
          const newPath = [...currentPath, node.id];

          if (node.id === targetId) {
            // Found the target theme - return all ancestors (excluding the target itself)
            return new Set(currentPath);
          }

          if (node.children && node.children.length > 0) {
            const found = findAncestors(node.children, targetId, newPath);
            if (found.size > 0) {
              return found;
            }
          }
        }
        return new Set<string>();
      };

      // Collect theme IDs: themes in events + their direct ancestors (NO descendants, NO siblings)
      const allRelevantIds = new Set<string>(themeIdsInEvents);

      console.log('[use-events-filter] Theme IDs in events:', Array.from(themeIdsInEvents));

      themeIdsInEvents.forEach(themeId => {
        const ancestors = findAncestors(themesTree, themeId);
        console.log(`[use-events-filter] Ancestors for ${themeId}:`, Array.from(ancestors));
        ancestors.forEach(ancestorId => allRelevantIds.add(ancestorId));
      });

      console.log('[use-events-filter] All relevant IDs (should only be themes + direct ancestors):', Array.from(allRelevantIds));

      /**
       * Filter tree to include only relevant themes (themes in events + ancestors)
       * Propagate colours from parents to children
       * Only include children if they are also in allRelevantIds
       */
      const filterTree = (nodes: ThemeNode[], parentColor?: string | null): ThemeNode[] => {
        const result: ThemeNode[] = [];

        for (const node of nodes) {
          if (allRelevantIds.has(node.id)) {
            // Use node colour if available and not empty, otherwise inherit from parent
            const nodeColor =
              node.color && node.color.trim() !== ''
                ? node.color
                : parentColor && parentColor.trim() !== ''
                  ? parentColor
                  : undefined;
            
            // Recursively filter children - only include if in allRelevantIds
            const filteredChildren = node.children ? filterTree(node.children, nodeColor) : [];
            
            result.push({
              ...node,
              color: nodeColor,
              children: filteredChildren,
            });
          }
        }

        return result;
      };

      return filterTree(themesTree);
    }

    // Fallback: extract unique themes from visible events (flat list)
    const themeMap = new Map<string, { id: string; name: string }>();

    visibleEventsForThemes.forEach(event => {
      const eventThemes = event.theme ?? [];
      eventThemes.forEach((theme: any) => {
        if (theme?.id && theme?.name && !themeMap.has(theme.id)) {
          themeMap.set(theme.id, {
            id: theme.id,
            name: theme.name,
          });
        }
      });
    });

    return Array.from(themeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events, themesTree, showPastEvents]);

  // Use filteredThemes as filteredThemesList for the return value
  const filteredThemesList = filteredThemes;

  // Helper: get all descendant IDs of given theme IDs (for branch filtering)
  const getAllDescendantIds = useMemo(() => {
    if (!themesTree || themesTree.length === 0) return new Map<string, string[]>();

    const descendantsMap = new Map<string, string[]>();

    const collectDescendants = (node: ThemeNode): string[] => {
      const descendants: string[] = [];

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          descendants.push(child.id);
          descendants.push(...collectDescendants(child));
        });
      }

      return descendants;
    };

    const traverse = (node: ThemeNode) => {
      descendantsMap.set(node.id, collectDescendants(node));
      if (node.children && node.children.length > 0) {
        node.children.forEach(traverse);
      }
    };

    themesTree.forEach(traverse);
    return descendantsMap;
  }, [themesTree]);

  // Filter events
  const filteredEvents = useMemo(() => {
    const now = new Date();

    const getEventThemeIds = (event: Event) =>
      (event.theme ?? [])
        .map((theme: any) => theme?.id)
        .filter((id: any): id is string => Boolean(id));

    return events.filter(event => {
      // Past events filter
      if (!showPastEvents) {
        const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
        if (!endTimestamp) return false;
        const eventEnd = new Date(endTimestamp * 1000);
        if (eventEnd < now) return false;
      }

      // Event type filter - OR logic (at least one of the selected types)
      if (selectedEventTypes.length > 0) {
        const normalizedTypes = normalizeEventType(event.eventType);
        const eventTypeIds = normalizedTypes.map(t => t?.id).filter(Boolean);
        const hasSelectedType = selectedEventTypes.some(typeId => eventTypeIds.includes(typeId));
        if (!hasSelectedType) return false;
      }

      // Theme filter - OR (at least one) or AND (all themes)
      // Support branch filtering: selecting a parent theme includes all its descendants
      if (selectedThemes.length > 0) {
        const eventThemeIds = getEventThemeIds(event);

        // Expand selected themes to include all descendants (for branch filtering)
        const expandedSelectedThemes = new Set<string>(selectedThemes);
        selectedThemes.forEach(themeId => {
          const descendants = getAllDescendantIds.get(themeId);
          if (descendants) {
            descendants.forEach(descId => expandedSelectedThemes.add(descId));
          }
        });

        if (themeFilterMode === 'OR') {
          const hasAnyTheme = Array.from(expandedSelectedThemes).some(themeId =>
            eventThemeIds.includes(themeId)
          );
          if (!hasAnyTheme) return false;
        } else {
          const hasAllThemes = selectedThemes.every(themeId => {
            // For AND mode, check if event has this theme OR any of its descendants
            const descendants = getAllDescendantIds.get(themeId) || [];
            return (
              eventThemeIds.includes(themeId) ||
              descendants.some(descId => eventThemeIds.includes(descId))
            );
          });
          if (!hasAllThemes) return false;
        }
      }

      // Search query
      if (!normalizedQuery) return true;

      // Search in title
      if (event.title.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in location
      if (event.eventLocation?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in themes
      if (event.theme?.some((theme: any) => theme?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      // Search in event types
      const normalizedTypes = normalizeEventType(event.eventType);
      if (normalizedTypes.some(type => type?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      // Search in contact name
      if (event.eventContactName?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      if (event.eventContact?.name?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in body content (strip HTML)
      const bodyText = stripHtml(event.body?.summary || event.body?.processed || '');
      if (bodyText.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return false;
    });
  }, [
    events,
    normalizedQuery,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents,
    getAllDescendantIds,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / safePageSize));

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    normalizedQuery,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents,
    safePageSize,
    events,
  ]);

  // Ensure current page doesn&apos;t exceed total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Paginated events
  const { visibleEvents, startIndex, endIndex } = useMemo(() => {
    const start = (currentPage - 1) * safePageSize;
    const end = Math.min(start + safePageSize, filteredEvents.length);
    return {
      visibleEvents: filteredEvents.slice(start, end),
      startIndex: start,
      endIndex: end,
    };
  }, [filteredEvents, currentPage, safePageSize]);

  // Actions - wrapped with startTransition for non-urgent updates
  const toggleEventType = (typeId: string) => {
    startTransition(() => {
      setSelectedEventTypes(prev =>
        prev.includes(typeId) ? prev.filter(id => id !== typeId) : [...prev, typeId]
      );
    });
  };

  const toggleTheme = (themeId: string) => {
    startTransition(() => {
      setSelectedThemes(prev =>
        prev.includes(themeId) ? prev.filter(id => id !== themeId) : [...prev, themeId]
      );
    });
  };

  /**
   * Toggle multiple themes at once (for hierarchical selection)
   * If adding, adds all themes; if removing, removes all themes
   */
  const toggleThemes = (themeIds: string[], shouldSelect?: boolean) => {
    startTransition(() => {
      setSelectedThemes(prev => {
        // Determine if we're adding or removing based on the first theme
        const isAdding = shouldSelect !== undefined 
          ? shouldSelect 
          : !prev.includes(themeIds[0]);

        if (isAdding) {
          // Add all theme IDs that aren't already selected
          const newIds = themeIds.filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        } else {
          // Remove all theme IDs
          return prev.filter(id => !themeIds.includes(id));
        }
      });
    });
  };

  const clearEventTypeSelection = () => {
    startTransition(() => {
      setSelectedEventTypes([]);
    });
  };

  const clearThemeSelection = () => {
    startTransition(() => {
      setSelectedThemes([]);
    });
  };

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  };

  const clearAllFilters = () => {
    startTransition(() => {
      setQuery('');
      setSelectedEventTypes([]);
      setSelectedThemes([]);
      setCurrentPage(1);
    });
  };

  // Wrapper for setQuery with transition
  const handleQueryChange = (newQuery: string) => {
    startTransition(() => {
      setQuery(newQuery);
    });
  };

  const totalResults = filteredEvents.length;
  const hasResults =
    totalResults > 0 ||
    normalizedQuery !== '' ||
    selectedEventTypes.length > 0 ||
    selectedThemes.length > 0;
  const hasActiveFilters =
    normalizedQuery !== '' || selectedEventTypes.length > 0 || selectedThemes.length > 0;

  return {
    query,
    selectedEventTypes,
    selectedThemes,
    toggleThemes,
    themeFilterMode,
    showPastEvents,
    currentPage,
    eventTypes,
    themes: filteredThemesList,
    themesTree, // Return complete tree for service creation
    visibleEvents,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    isPending,
    setQuery: handleQueryChange,
    toggleEventType,
    toggleTheme,
    clearEventTypeSelection,
    clearThemeSelection,
    setThemeFilterMode,
    setShowPastEvents,
    handlePageChange,
    clearAllFilters,
  };
}
